import { Switch } from '@headlessui/react';
import clsx from 'clsx';
import { useEffect, useState } from 'react';

import {
  isWebPushSubscribed,
  subscribeToWebPush,
  unsubscribeFromWebPush,
  webPushSupported,
} from '@/renderer/utils/webPush';

/**
 * Enable/disable background Web Push for THIS browser (#1669). Unlike native
 * push (per-account, OS-owned — see PushNotifications), a Web Push subscription
 * is per-browser, so this toggle reflects and controls the current browser's
 * own subscription. On → request permission (if needed) + subscribe; off →
 * unsubscribe (removes the subscription server-side and locally).
 *
 * Rendered only where the browser supports Web Push. Note the service worker is
 * production-only, so this is inert in dev/staging (the toggle stays off and
 * can't enable) — expected.
 */
export default function WebPushSwitch() {
  const [supported, setSupported] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);

  const refresh = async () => {
    const ok = webPushSupported();
    setSupported(ok);
    setDenied(typeof Notification !== 'undefined' && Notification.permission === 'denied');
    setSubscribed(ok ? await isWebPushSubscribed() : false);
    setLoading(false);
  };

  useEffect(() => {
    void refresh();
    // Re-check when the user returns to the tab (e.g. after changing the
    // browser's site permission), mirroring PushNotifications.
    const onVisible = () => {
      if (typeof document === 'undefined' || document.visibilityState === 'visible') {
        void refresh();
      }
    };
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const onChange = async (next: boolean) => {
    setLoading(true);
    try {
      if (next) {
        // Request the OS/browser permission from within this click gesture, then
        // subscribe. subscribeToWebPush itself only proceeds when granted.
        let permission = typeof Notification !== 'undefined' ? Notification.permission : 'denied';
        if (permission === 'default') {
          permission = await Notification.requestPermission();
        }
        if (permission !== 'granted') {
          setDenied(permission === 'denied');
          setSubscribed(false);
          return;
        }
        setDenied(false);
        setSubscribed(await subscribeToWebPush());
      } else {
        await unsubscribeFromWebPush();
        setSubscribed(false);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!supported) {
    return (
      <p className="text-sm text-zinc-500 dark:text-zinc-400">
        This browser doesn&apos;t support background notifications. You&apos;ll still receive
        everything on raspicks.com.
      </p>
    );
  }

  return (
    <div className="flex items-start space-x-4">
      <Switch
        checked={subscribed}
        onChange={(next: boolean) => {
          void onChange(next);
        }}
        className={clsx(
          subscribed ? 'bg-emerald-500 dark:bg-emerald-400' : 'bg-zinc-200 dark:bg-zinc-700',
          'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full',
          loading && 'animate-pulse',
        )}
        disabled={loading}
      >
        <span className="sr-only">Browser notifications</span>
        <span
          className={`${
            subscribed ? 'translate-x-6' : 'translate-x-1'
          } inline-block size-4 rounded-full bg-white transition`}
        />
      </Switch>
      <div className="flex flex-col space-y-2">
        <div className="text-sm text-zinc-500 dark:text-zinc-400">
          {denied
            ? 'Blocked. Notifications are turned off for this site in your browser settings — re-enable them there, then toggle this on.'
            : subscribed
              ? 'On — release warnings will show on this browser (or installed app) even when the tab is closed.'
              : 'Off. Turn on to get release warnings on this browser even when the tab is closed.'}
        </div>
      </div>
    </div>
  );
}
